import has from 'lodash/has';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex } from 'roo-ui/components';
import ButtonLabel from './ButtonLabel';
import { useDataLayer } from 'hooks/useDataLayer';
import { getQffAuth } from 'lib/qffAuth';
import { addMemberFavourite, removeMemberFavourite } from 'store/memberFavourites/memberFavouritesActions';
import { getMemberFavourites, getMemberFavouritesStatus, getMemberFavouritesError } from 'store/memberFavourites/memberFavouritesSelectors';
import { getProperty } from 'store/property/propertySelectors';
import { getIsAuthenticated } from 'store/user/userSelectors';
import { FavouriteButton, FavouriteIcon, NonFavouriteIcon } from './primitives';
import { status } from 'store/memberFavourites/memberFavouritesReducer';

const login = async () => {
  try {
    const qffAuth = await getQffAuth();
    qffAuth.showLoginModal();
  } catch (e) {
    // skip
  }
};

const MemberFavouritesButton = () => {
  const dispatch = useDispatch();
  const { emitInteractionEvent } = useDataLayer();

  const [tempFavouritePropertyId, setTempFavouritePropertyId] = useState();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const memberFavourites = useSelector(getMemberFavourites);
  const memberFavouritesStatus = useSelector(getMemberFavouritesStatus);
  const memberFavouritesError = useSelector(getMemberFavouritesError);
  const property = useSelector(getProperty);

  const propertyId = property?.id;
  const isFavourite = propertyId && has(memberFavourites, propertyId);
  const isDisabled = memberFavouritesStatus === status.PENDING;

  const handleAddFavourite = useCallback(async () => {
    emitInteractionEvent({ type: 'Member Favourites Button', value: 'Add Favourite Selected' });

    if (isAuthenticated) {
      dispatch(addMemberFavourite({ propertyId }));
    } else {
      setTempFavouritePropertyId(propertyId);

      await login();
    }
  }, [dispatch, emitInteractionEvent, isAuthenticated, propertyId]);

  const handleRemoveFavourite = useCallback(() => {
    emitInteractionEvent({ type: 'Member Favourites Button', value: 'Remove Favourite Selected' });
    dispatch(removeMemberFavourite(propertyId));
  }, [dispatch, emitInteractionEvent, propertyId]);

  useEffect(() => {
    if (isAuthenticated && !!memberFavourites && tempFavouritePropertyId) {
      if (!isFavourite) {
        dispatch(addMemberFavourite({ propertyId: tempFavouritePropertyId }));
      }
      setTempFavouritePropertyId(null);
    }
  }, [dispatch, isAuthenticated, tempFavouritePropertyId, isFavourite, memberFavourites]);

  return (
    <UnconnectedMemberFavouritesButton
      isAuthenticated={isAuthenticated}
      isDisabled={isDisabled}
      hasError={!!memberFavouritesError}
      isFavourite={isFavourite}
      onClick={isFavourite ? handleRemoveFavourite : handleAddFavourite}
    />
  );
};

export const UnconnectedMemberFavouritesButton = ({ isAuthenticated, isDisabled, hasError, isFavourite, onClick }) => (
  <Flex alignItems="center" justifyContent={['space-between', 'flex-end']}>
    <ButtonLabel isAuthenticated={isAuthenticated} isFavourite={isFavourite} hasError={hasError} />
    <FavouriteButton disabled={isDisabled} onClick={onClick}>
      {hasError && <NonFavouriteIcon name="favoriteStrike" size={[24, 32]} title="Error adding to Dream planner icon" />}
      {!hasError && isFavourite && <FavouriteIcon name="favorite" size={[24, 32]} title="Remove from Dream Planner Icon" />}
      {!hasError && !isFavourite && <NonFavouriteIcon name="favoriteBorder" size={[24, 32]} title="Add to Dream Planner Icon" />}
    </FavouriteButton>
  </Flex>
);

UnconnectedMemberFavouritesButton.propTypes = {
  isAuthenticated: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasError: PropTypes.bool,
  isFavourite: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

UnconnectedMemberFavouritesButton.defaultProps = {
  isAuthenticated: false,
  isDisabled: false,
  hasError: false,
  isFavourite: false,
};

export default MemberFavouritesButton;
